import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, roleHome } from "@/context/AuthContext";
import { Brand } from "@/components/SiteHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, User, Building2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const SIDE_IMG = "https://images.unsplash.com/photo-1777269749032-d8d458ae594d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(roleHome(user), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    const pending = searchParams.get("join") || localStorage.getItem("pending_join");
    if (pending && res.user.role === "patient") {
      try {
        await api.post("/patient/join", null, { params: { clinic_id: pending } });
        localStorage.removeItem("pending_join");
        navigate(`/dashboard?clinic=${pending}`, { replace: true });
        return;
      } catch (err) {
        toast.error("We couldn't link that business to your account. Please try the check-in link again.");
      }
    }
    navigate(roleHome(res.user), { replace: true });
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto w-full max-w-sm"
        >
          <Brand />
          <h1 className="mt-10 text-3xl font-bold tracking-tight text-slate-900">Welcome back</h1>
          <p className="mt-2 text-slate-500">Sign in to access your patient paperwork.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="login-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="mb-2 block font-semibold text-slate-700">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-2 block font-semibold text-slate-700">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={loading} className="h-12 w-full bg-[#0F52BA] text-base hover:bg-[#0B3D8A]" data-testid="login-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 border-t border-slate-100 pt-6">
            <p className="text-center text-sm font-medium text-slate-500">Don't have an account?</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Link
                to="/register?type=user"
                data-testid="goto-register-personal"
                className="group flex flex-col rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-[#0F52BA] hover:bg-[#0F52BA]/5"
              >
                <span className="flex items-center gap-2 font-semibold text-slate-900">
                  <User className="h-4 w-4 text-[#0F52BA]" /> Personal account
                </span>
                <span className="mt-1 text-xs text-slate-500">Free — for patients &amp; clients filling out paperwork</span>
              </Link>
              <Link
                to="/register"
                data-testid="goto-register-business"
                className="group flex flex-col rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-[#0F52BA] hover:bg-[#0F52BA]/5"
              >
                <span className="flex items-center gap-2 font-semibold text-slate-900">
                  <Building2 className="h-4 w-4 text-[#0F52BA]" /> Business account
                </span>
                <span className="mt-1 text-xs text-slate-500">Start a free 14-day trial for your Blitzer business</span>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative hidden lg:block">
        <img src={SIDE_IMG} alt="Nimbus" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D8A]/70 via-[#0F52BA]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-sm text-white">
          <p className="text-2xl font-semibold leading-snug">Your care begins before you arrive.</p>
          <p className="mt-2 text-blue-100">Complete your paperwork securely, on your own time.</p>
        </div>
      </div>
    </div>
  );
}
